import React, { useState } from 'react'
import { Check, Loader2, RefreshCw, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useToast } from '@/components/ui/use-toast'
import { DIALOG_CLASSES } from '@/lib/theme'
import { cn } from '@/lib/utils'
import { money } from '@/lib/dealTypes'
import { useRespondToApplicationMutation } from '@/redux/marketerApi'

type Props = {
  applicationId: string
  athleteName: string
  dealType: string
  ask: number | null
  /** After the brand answers: the new status */
  onDone?: (status: 'accepted' | 'offered' | 'rejected') => void
  size?: 'sm' | 'default'
  className?: string
  /** Accept across the top, Counter and Pass under it (the Incoming tab's cards) */
  stacked?: boolean
}

/**
 * Accept, Counter or Pass on an athlete's submission. Counter opens a dialog for the new amount
 * and a note; Pass opens a short confirm with an optional note. Used in the Submissions inbox
 * and on the collaboration page while a deal is waiting on the brand.
 */
export const ApplicationActions: React.FC<Props> = ({
  applicationId,
  athleteName,
  dealType,
  ask,
  onDone,
  size = 'default',
  className,
  stacked = false,
}) => {
  const { toast } = useToast()
  const [respond, { isLoading }] = useRespondToApplicationMutation()
  const [dialog, setDialog] = useState<'counter' | 'pass' | null>(null)
  const [amount, setAmount] = useState(ask != null ? String(Math.round(ask)) : '')
  const [note, setNote] = useState('')
  const firstName = athleteName.split(' ')[0]
  const paid = dealType !== 'gifting'

  const send = async (action: 'accept' | 'counter' | 'pass') => {
    try {
      const res = await respond({
        id: applicationId,
        action,
        message: note.trim() || undefined,
        paymentAmount: action === 'counter' ? Number(amount) : undefined,
      }).unwrap()
      setDialog(null)
      setNote('')
      toast({
        title:
          action === 'accept'
            ? `${firstName} is in`
            : action === 'counter'
              ? `Counter sent to ${firstName}`
              : `Passed on ${firstName}`,
        description:
          action === 'accept'
            ? 'The deal is live. Their first step is on the deal page.'
            : action === 'counter'
              ? 'They can accept, counter again, or pass.'
              : 'They have been told. No hard feelings.',
      })
      onDone?.(res.status)
    } catch (err: any) {
      toast({
        title: "Couldn't send",
        description: err?.data?.error ?? 'Try again in a moment.',
        variant: 'destructive',
      })
    }
  }

  return (
    <>
      <div className={cn(stacked ? 'grid grid-cols-2 gap-2' : 'flex flex-wrap gap-2', className)}>
        <Button
          variant="lime"
          size={size}
          disabled={isLoading}
          onClick={() => send('accept')}
          className={stacked ? 'col-span-2 h-11' : 'min-w-24'}
        >
          {isLoading && !dialog ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Check className="h-4 w-4" />
          )}
          Accept
        </Button>
        {paid ? (
          <Button
            variant="dark-outline"
            size={size}
            disabled={isLoading}
            onClick={() => setDialog('counter')}
            className={stacked ? 'h-11 bg-card' : undefined}
          >
            <RefreshCw className="h-4 w-4" />
            Counter
          </Button>
        ) : null}
        <Button
          variant={stacked ? 'dark-outline' : 'ghost'}
          size={size}
          disabled={isLoading}
          onClick={() => setDialog('pass')}
          className={
            stacked
              ? cn('h-11 bg-card', paid ? '' : 'col-span-2')
              : 'text-gray-600 hover:bg-foreground/10 hover:text-foreground'
          }
        >
          <X className="h-4 w-4" />
          Pass
        </Button>
      </div>

      <Dialog open={dialog === 'counter'} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className={cn('sm:max-w-md', DIALOG_CLASSES.dark)}>
          <DialogHeader>
            <DialogTitle>Counter {firstName}'s ask</DialogTitle>
            <DialogDescription className="text-gray-600">
              They asked for {money(ask)}. Your counter goes back to them as an offer they can
              accept, counter or pass.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="counter-amount" className="text-gray-700">
                Your offer{dealType === 'licensing' ? ' per month' : ''}
              </Label>
              <div className="relative">
                <span className="absolute left-3 top-2.5 text-sm text-gray-500">$</span>
                <Input
                  id="counter-amount"
                  type="number"
                  inputMode="numeric"
                  min={0}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="border-foreground/15 bg-card pl-7 text-foreground"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="counter-note" className="text-gray-700">
                Note to {firstName}
              </Label>
              <Textarea
                id="counter-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                placeholder={`Love the pitch. Our budget for this is ${amount ? `$${Number(amount).toLocaleString()}` : 'a bit lower'}. Can we do that?`}
                className="border-foreground/15 bg-card text-foreground placeholder:text-gray-500"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialog(null)} className="text-gray-700">
              Cancel
            </Button>
            <Button
              variant="lime"
              disabled={isLoading || !amount || note.trim().length < 3}
              onClick={() => send('counter')}
            >
              {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Send counter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dialog === 'pass'} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className={cn('sm:max-w-md', DIALOG_CLASSES.dark)}>
          <DialogHeader>
            <DialogTitle>Pass on {firstName}?</DialogTitle>
            <DialogDescription className="text-gray-600">
              They'll be told you went another way. A short reason helps them next time.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            placeholder="Optional: Thanks for applying. We're looking for athletes in Ohio this round."
            className="border-foreground/15 bg-card text-foreground placeholder:text-gray-500"
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialog(null)} className="text-gray-700">
              Keep it open
            </Button>
            <Button variant="destructive" disabled={isLoading} onClick={() => send('pass')}>
              {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Pass
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default ApplicationActions
